import React, { useEffect, useState } from 'react';
import { PrimaryButton, TertiaryButton } from '@workday/canvas-kit-react/button';
import { Subtext } from '@workday/canvas-kit-react/text';
import { ID_MOBILE_NUMBER, ID_PHONE_ENTRY_BUTTON, ID_PHONE_SKIP_BUTTON } from '../../../autoIds';
import { autoId, CardView, phoneNumberRegex } from '@workday/upc-common';
import { PhoneNumberTextField } from '../../../shared/phone-number-text-field/phone-number-text-field';
import styles from './phone-entry.module.scss';
import { useForm } from 'react-hook-form';
import { useEnrollmentPhoneInitMutation, useEnrollmentPhoneSkipMutation } from '../../../slices/auth-api.slice';
import { CountryCode, findCountryCode } from '../../../utils/countryCodes';
import { useSelector } from 'react-redux';
import { selectPhoneContact } from '../../../slices/registration.slice';
import { useTranslation } from 'react-i18next';
import { OverflowTooltip } from '@workday/canvas-kit-react/tooltip';

interface PhoneEntryState {
    mobileNumber: string;
}

export function PhoneEntry({ steps }) {
    const phoneContact = useSelector(selectPhoneContact);

    const { t } = useTranslation('auth');

    const { formState, handleSubmit, register } = useForm<PhoneEntryState>({
        reValidateMode: 'onSubmit',
        defaultValues: {
            [ID_MOBILE_NUMBER]: phoneContact?.number
        }
    });

    const [countryCode, setCountryCode] = useState<CountryCode>(() => {
        return findCountryCode(phoneContact);
    });

    const { ref: phoneNumberInputRef, ...phoneNumberInputProps } = register(ID_MOBILE_NUMBER, {
        minLength: {
            value: 10,
            message: t('ManagePhoneNumbers.phoneEntryNumberValidationError', { ns: 'shared' })
        },
        required: t('Required.fieldGeneric', { ns: 'shared' }),
        validate: value =>
            !!value.match(phoneNumberRegex) || t('ManagePhoneNumbers.phoneEntryNumberValidationError', { ns: 'shared' })
    });

    const phoneErrorMessage = formState.errors.mobileNumber?.message;

    const [enrollmentPhoneInit, enrollmentPhoneInitMutation] = useEnrollmentPhoneInitMutation();
    const [enrollmentPhoneSkip, enrollmentPhoneSkipMutation] = useEnrollmentPhoneSkipMutation();

    useEffect(() => {
        if (enrollmentPhoneInitMutation.isSuccess) {
            steps.next();
        }
    }, [enrollmentPhoneInitMutation, steps]);

    function onSubmit(data) {
        enrollmentPhoneInit({
            countryCode: countryCode.code,
            number: data[ID_MOBILE_NUMBER]
        });
    }

    function onSkipClick(e) {
        e.preventDefault();
        enrollmentPhoneSkip();
    }

    const disabled = enrollmentPhoneInitMutation.isLoading || enrollmentPhoneSkipMutation.isLoading;

    return (
        <div className={styles.phoneNumberEntry}>
            <h1 className={styles.title}>{t('PhoneEntry.title')}</h1>
            <p className={styles.body}>{t('PhoneEntry.subTitle')}</p>
            <CardView className={styles.card}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <PhoneNumberTextField
                        {...phoneNumberInputProps}
                        countryCode={countryCode}
                        onCountryCodeChange={code => setCountryCode(code)}
                        defaultValue={phoneContact?.number}
                        autoFocus={true}
                        errorMessage={phoneErrorMessage}
                        grow={true}
                        id={ID_MOBILE_NUMBER}
                        isDisabled={disabled}
                        isInvalid={!!phoneErrorMessage}
                        label={
                            <>
                                {t('PhoneEntry.mobileNumberLabel')}
                                <Subtext size='medium' style={{ marginTop: 0 }}>
                                    {t('PhoneEntry.limitedCountries')}
                                </Subtext>
                            </>
                        }
                        countryCodeLabel={t('PhoneEntry.countryCode')}
                        phoneNumberLabel={t('PhoneEntry.mobileNumberLabel')}
                        placeholder={t('PhoneEntry.mobileNumberPlaceholder')}
                        ref={phoneNumberInputRef}
                    />
                    <OverflowTooltip>
                        <PrimaryButton
                            {...autoId(ID_PHONE_ENTRY_BUTTON)}
                            className={styles.button}
                            disabled={disabled}
                            grow={true}
                            size='large'
                            type='submit'>
                            {t('PhoneEntry.submitButton')}
                        </PrimaryButton>
                    </OverflowTooltip>
                    <TertiaryButton
                        {...autoId(ID_PHONE_SKIP_BUTTON)}
                        onClick={onSkipClick}
                        className={styles.skip}
                        size='large'
                        type='button'>
                        {t('PhoneEntry.skipButton')}
                    </TertiaryButton>
                    <Subtext className={styles.subtext} size='large'>
                        {t('PhoneEntry.mobileConsent')}
                    </Subtext>
                </form>
            </CardView>
        </div>
    );
}
